import { FormGroup } from '@angular/forms';
import { isAfter } from 'date-fns';
import { isEqual } from 'lodash-es';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { addRemoveError, getControl } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { toNum } from '@shared/helpers/math.helper';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { Revisione53DatiBusinessSelectors } from '../../../store/selectors/dati-business.selectors';
import { getDatiIdentificativiControl, getDatiIdentificativiGroup } from './helpers/dati-identificativi-form-validators.helper';
import { formPaths } from '@shared/constants/form-paths.const';

export const datiIdentificativiCrossSectionRules = (store: Store<Revisione53State>): FormGroupRules => ({
    my_ModoDiAssociazione: (form: FormGroup) => {
        const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
        const my_ModoDiAssociazioneControl = getControl(form, `${formPaths.schedaCoopTrasporto}.my_ModoDiAssociazione`);
        const my_TipoSezEnte: string = getControl(datiIdentificativiGroup, 'my_TipoSezEnte')?.value;
        const my_CkAltreCoopAllegaSchedaTrasporto: string = getControl(form, `${formPaths.schedaAltreCoop}.my_CkAltreCoopAllegaSchedaTrasporto`)?.value;
        const errors = my_ModoDiAssociazioneControl.errors ? my_ModoDiAssociazioneControl.errors : {};
        const showError = isNilOrEmpty(my_ModoDiAssociazioneControl?.value) && (my_TipoSezEnte === '8' || (my_TipoSezEnte === '10' && my_CkAltreCoopAllegaSchedaTrasporto === '1'));

        addRemoveError(showError, errors, 'my_ModoDiAssociazione', {
            status: REQUIRED_STATUS,
        });

        my_ModoDiAssociazioneControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_DataCompilaz: (form: FormGroup) => {
        const my_DataCompilazControl = getControl(form, `${formPaths.conclusioni}.my_DataCompilaz`);
        const my_Anno2: string = getDatiIdentificativiControl(form, 'my_Anno2')?.value;
        const errors = my_DataCompilazControl.errors ? my_DataCompilazControl.errors : {};
        const value: string = my_DataCompilazControl.value;
        const anno2Num = toNum(my_Anno2);
        const compilazDate = isNilOrEmpty(value) ? null : new Date(value);
        const isTooFarInFuture = compilazDate !== null && isAfter(compilazDate, new Date('2079-06-06'));
        const isAfterCutoff = compilazDate !== null && !isTooFarInFuture && anno2Num > 0 && isAfter(compilazDate, new Date(`${anno2Num + 1}-03-31`));

        addRemoveError(isNilOrEmpty(value), errors, 'my_DataCompilaz', { status: REQUIRED_STATUS });
        addRemoveError(isTooFarInFuture, errors, 'my_DataCompilazInvalid', { status: 'Data non consentita' });
        addRemoveError(isAfterCutoff, errors, 'my_DataCompilazInvalid', { status: 'La revisione non può terminare dopo il mese di marzo' });

        my_DataCompilazControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CodAttiv: (form: FormGroup) => {
        const my_CodAttivControl = getDatiIdentificativiControl(form, 'my_CodAttiv');
        const my_CodAttiv: string = my_CodAttivControl?.value;
        const my_ValidaCodiceISTAT: string = getControl(form, 'my_ValidaCodiceISTAT')?.value;
        const errors = my_CodAttivControl.errors ? my_CodAttivControl.errors : {};
        const hasCode = !isNilOrEmpty(my_CodAttiv);
        let attivita: { descrizione?: string } | undefined;

        if (hasCode) {
            store
                .select(Revisione53DatiBusinessSelectors.getCodISTAT(my_CodAttiv))
                .pipe(take(1))
                .subscribe((found) => (attivita = found));
        }

        const nonPresente = hasCode && isNilOrEmpty(attivita);
        const incompleto = hasCode && !nonPresente && my_CodAttiv.trim().length < 8;

        addRemoveError(!hasCode, errors, 'my_CodAttiv', {
            status: REQUIRED_STATUS,
            message: 'Il codice ISTAT è un campo obbligatorio',
        });

        addRemoveError(nonPresente, errors, 'my_CodAttivNonPresente', {
            status: 'Codice ISTAT non presente',
            message: "Il codice ISTAT non è presente nell'elenco dei codici validi",
        });

        addRemoveError(incompleto, errors, 'my_CodAttivIncompleto', {
            status: 'Attenzione: specificare ulteriore codice ISTAT',
            message: "Il codice ISTAT corrisponde all'attività: " + attivita?.descrizione + '. Specificare ulteriormente',
        });

        addRemoveError(my_ValidaCodiceISTAT === '0' && !nonPresente && !incompleto, errors, 'my_CodAttivNonValido', {
            status: 'Attenzione: codice attività non corrispondente',
            message: 'Il codice attività non corrisponde ai codici ISTAT conosciuti',
        });

        my_CodAttivControl.setErrors(isEqual(errors, {}) ? null : errors);
    },
});
